import React, { useState, useRef, useEffect } from 'react';
import { Play, RotateCcw, Copy, Check, ChevronDown, Terminal, Loader2, Clock, Cpu, History, Plus, Share2, Trash2, X } from 'lucide-react';
import Editor from '@monaco-editor/react';
import { toast } from 'react-hot-toast';
import api, { shareSandboxSession, getSandboxSession } from '../../utils/api';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import LZString from 'lz-string';

const LANGUAGES = [
  { value: 'python', label: 'Python', icon: '' },
  { value: 'javascript', label: 'JavaScript', icon: '' },
  { value: 'java', label: 'Java', icon: '' },
  { value: 'cpp', label: 'C++', icon: '' },
  { value: 'go', label: 'Go', icon: '' },
];

// Simple language detection from code content
const detectLanguage = (code) => {
  if (!code) return 'python';
  const c = code.trim();
  if (c.includes('#include') || c.includes('using namespace std') || c.includes('int main(')) return 'cpp';
  if (c.includes('public class') || c.includes('public static void main') || c.includes('System.out.println')) return 'java';
  if (c.includes('package main') || c.includes('func main()') || c.includes('fmt.Println')) return 'go';
  if (c.includes('console.log') || c.includes('function ') || c.includes('const ') || c.includes('let ') || c.includes('=>')) return 'javascript';
  if (c.includes('def ') || c.includes('print(') || c.includes('import ') || c.includes('class ') || c.includes('self.')) return 'python';
  return 'python';
};

const CodePlayground = ({ 
  initialCode = '', 
  initialLanguage = '', 
  enableHistory = false, 
  persistenceKey = 'code_playground_sessions' 
}) => {
  const [code, setCode] = useState(initialCode);
  const [originalCode] = useState(initialCode);
  const [language, setLanguage] = useState(initialLanguage || detectLanguage(initialCode));
  const [input, setInput] = useState('');
  const [output, setOutput] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const editorRef = useRef(null);

  const [isDarkMode, setIsDarkMode] = useState(() => {
    return document.documentElement.classList.contains('dark');
  });

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    return () => observer.disconnect();
  }, []);

  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [deleteSessionId, setDeleteSessionId] = useState(null);
  const [shareSessionUrl, setShareSessionUrl] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [hoveredSessionId, setHoveredSessionId] = useState(null);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);

  useEffect(() => {
    // If history is disabled, we don't load anything from localStorage
    if (!enableHistory) {
      setCode(initialCode);
      setLanguage(initialLanguage || detectLanguage(initialCode));
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const shareId = params.get('id');
    const shareData = params.get('share');
    let initialSessionLoaded = false;
    
    const handleSharedSession = (decoded) => {
      if (decoded && decoded.code && decoded.language) {
        setCode(decoded.code);
        setLanguage(decoded.language);
        initialSessionLoaded = true;
        window.history.replaceState({}, document.title, window.location.pathname);
        createNewSession(decoded.code, decoded.language, "Shared Session");
      }
    };

    const loadSharedContent = async () => {
      if (shareId) {
        try {
          const data = await getSandboxSession(shareId);
          handleSharedSession(data);
        } catch (e) {
          console.error("Failed to fetch shared session from backend", e);
        }
      } else if (shareData) {
        try {
          let decoded;
          try {
            const decompressed = LZString.decompressFromEncodedURIComponent(shareData);
            if (decompressed) {
              decoded = JSON.parse(decompressed);
            } else {
              throw new Error('Not LZ string');
            }
          } catch (e) {
            decoded = JSON.parse(decodeURIComponent(atob(shareData)));
          }
          handleSharedSession(decoded);
        } catch (e) {
          console.error("Failed to parse shared code string", e);
        }
      }
    };

    loadSharedContent();
    
    if (!initialSessionLoaded) {
      const savedSessions = localStorage.getItem(persistenceKey);
      if (savedSessions) {
        try {
          const parsed = JSON.parse(savedSessions);
          setSessions(parsed);
          if (parsed.length > 0) {
            const mostRecent = parsed.sort((a, b) => b.updatedAt - a.updatedAt)[0];
            setCurrentSessionId(mostRecent.id);
            setCode(mostRecent.code);
            setLanguage(mostRecent.language);
            initialSessionLoaded = true;
          }
        } catch (e) {
          console.error("Failed to parse saved sessions", e);
        }
      }
    }
    
    if (!initialSessionLoaded) {
      createNewSession(
        initialCode || '// Welcome to the Sandbox!\n// Write code here and add Custom Input to test specific edge cases.',
        initialLanguage || 'javascript',
        "Session 1"
      );
    }
  }, [enableHistory, persistenceKey, initialCode, initialLanguage]);

  useEffect(() => {
    if (!enableHistory || !currentSessionId) return;
    setSessions(prev => {
      const newSessions = prev.map(s => {
        if (s.id === currentSessionId) {
          return { ...s, code, language, updatedAt: Date.now() };
        }
        return s;
      });
      localStorage.setItem(persistenceKey, JSON.stringify(newSessions));
      return newSessions;
    });
  }, [code, language, currentSessionId, enableHistory, persistenceKey]);

  const createNewSession = (initialCodeStr = '', initialLang = 'javascript', namePrefix = "Session") => {
    const newId = Date.now().toString() + Math.random().toString(36).substr(2, 5);
    if (enableHistory) {
      setSessions(prev => {
        const newSession = {
          id: newId,
          name: `${namePrefix} ${prev.length > 0 && namePrefix === "Session" ? prev.length + 1 : ''}`.trim(),
          code: initialCodeStr,
          language: initialLang,
          updatedAt: Date.now(),
          createdAt: Date.now(),
        };
        const updated = [...prev, newSession];
        localStorage.setItem(persistenceKey, JSON.stringify(updated));
        return updated;
      });
    }
    setCurrentSessionId(newId);
    setCode(initialCodeStr);
    setLanguage(initialLang);
  };


  const loadSession = (id) => {
    const session = sessions.find(s => s.id === id);
    if (session) {
      setCurrentSessionId(id);
      setCode(session.code);
      setLanguage(session.language);
      setShowHistory(false);
    }
  };

  const clearSessions = () => {
    if (window.confirm("Are you sure you want to clear all history? This cannot be undone.")) {
      localStorage.removeItem(persistenceKey);
      setSessions([]);
      setCurrentSessionId(null);
      createNewSession(
        '// Welcome to the Sandbox!\n// Write code here and add Custom Input to test specific edge cases.',
        'javascript',
        "Session 1"
      );
      setShowHistory(false);
      toast.success("History cleared");
    }
  };

  const shareSession = async (e, sessionToShare = null) => {
    if (e) e.stopPropagation();
    try {
      setIsGeneratingLink(true);
      const targetCode = sessionToShare ? sessionToShare.code : code;
      const targetLang = sessionToShare ? sessionToShare.language : language;
      
      const response = await shareSandboxSession({
        code: targetCode,
        language: targetLang
      });

      if (response && response.id) {
        const url = `${window.location.origin}${window.location.pathname}?id=${response.id}`;
        setShareSessionUrl(url);
        setShowHistory(false);
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (err) {
      toast.error("Failed to generate share link");
      console.error(err);
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const handleCopyShareLink = () => {
    if (shareSessionUrl) {
      navigator.clipboard.writeText(shareSessionUrl);
      setLinkCopied(true);
      toast.success("Share link copied!");
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  const requestDeleteSession = (e, id) => {
    e.stopPropagation();
    setDeleteSessionId(id);
  };

  const confirmDeleteSession = () => {
    if (!deleteSessionId) return;
    
    const newSessions = sessions.filter(s => s.id !== deleteSessionId);
    setSessions(newSessions);
    localStorage.setItem('code_playground_sessions', JSON.stringify(newSessions));
    
    if (currentSessionId === deleteSessionId) {
      if (newSessions.length > 0) {
        const mostRecent = newSessions.sort((a, b) => b.updatedAt - a.updatedAt)[0];
        loadSession(mostRecent.id);
      } else {
        setCurrentSessionId(null);
        createNewSession(
          '// Welcome to the Sandbox!\n// Write code here and add Custom Input to test specific edge cases.',
          'javascript',
          "Session 1"
        );
      }
    }
    toast.success("Session deleted");
    setDeleteSessionId(null);
  };

  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;

    // Add support for Cmd+Enter / Ctrl+Enter to trigger code execution directly inside Monaco Editor
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => {
      handleRun();
    });
  };

  const handleRun = async () => {
    // If we're already running or code is empty, do nothing
    // We get the actual code from the editor if possible to ensure we have the very latest state
    const currentCode = editorRef.current ? editorRef.current.getValue() : code;
    
    if (isRunning || !currentCode.trim()) return;

    setIsRunning(true);
    setOutput(null);
    try {
      const res = await api.post('/api/code/execute', {
        code: currentCode,
        language,
        input,
      });
      setOutput(res.data);
    } catch (err) {
      setOutput({
        error: err.response?.data?.error || 'Execution failed. Please try again.',
        status: 'Error',
      });
    } finally {
      setIsRunning(false);
    }
  };

  const handleCopy = () => {
    const currentCode = editorRef.current ? editorRef.current.getValue() : code;
    navigator.clipboard.writeText(currentCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleReset = () => {
    setCode(originalCode);
    setOutput(null);
    setInput('');
  };

  // Use calc to ensure the editor fits perfectly on the screen without overflowing
  const editorHeight = 'calc(100vh - 400px)';

  return (
    <div className="my-5 rounded-xl bg-white overflow-hidden border border-emerald-200 shadow-[0_4px_24px_rgba(16,185,129,0.06)] font-sans relative group hover:border-emerald-300 transition-colors duration-300">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-emerald-50/50 border-b border-emerald-100/60">
        <div className="flex items-center gap-4">
          {/* Traffic Lights */}
          <div className="flex gap-1.5 opacity-60 group-hover:opacity-100 transition-opacity">
            <div className="w-2.5 h-2.5 rounded-full bg-rose-400" />
            <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
          </div>
          
          {/* Language selector */}
          <div className="relative">
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="appearance-none bg-white text-emerald-900 text-xs font-bold px-3 py-1.5 pr-7 rounded-md outline-none cursor-pointer border border-emerald-200 focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 shadow-sm transition-all"
            >
              {LANGUAGES.map((l) => (
                <option key={l.value} value={l.value}>
                  {l.label}
                </option>
              ))}
            </select>
            <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-emerald-600 pointer-events-none stroke-[3]" />
          </div>
        </div>

        <div className="flex items-center gap-1 relative">
          {enableHistory && (
            <button
              onClick={() => setShowHistory(!showHistory)}
              className={`p-1.5 rounded-md hover:bg-emerald-100/80 transition-colors tooltip-trigger ${showHistory ? 'text-emerald-700 bg-emerald-100' : 'text-slate-400 hover:text-emerald-700'}`}
              title="Session History"
            >
              <History size={15} />
            </button>
          )}
          
          {/* History Dropdown */}
          {enableHistory && showHistory && (
            <div className="absolute top-10 right-0 w-72 bg-white rounded-xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)] border border-emerald-100 z-50 overflow-hidden transform origin-top-right transition-all">
              <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-100">
                <h3 className="font-bold text-xs text-slate-700tracking-wider flex items-center gap-2">
                  <History size={13} className="text-emerald-600"/> Sessions
                </h3>
                <button onClick={() => setShowHistory(false)} className="text-slate-400 hover:text-slate-600 bg-white rounded-md p-1 border border-slate-200 shadow-sm transition-colors"><X size={12}/></button>
              </div>
              <div className="max-h-[280px] overflow-y-auto p-2 scrollbar-thin scrollbar-thumb-slate-200">
                {sessions.sort((a, b) => b.updatedAt - a.updatedAt).map(session => (
                  <div
                    key={session.id}
                    onClick={() => loadSession(session.id)}
                    onMouseEnter={() => setHoveredSessionId(session.id)}
                    onMouseLeave={() => setHoveredSessionId(null)}
                    className={`w-full text-left px-3 py-2.5 rounded-lg text-sm mb-1 transition-all flex flex-col relative cursor-pointer ${session.id === currentSessionId ? 'bg-emerald-50 border-emerald-200 border shadow-sm' : 'hover:bg-slate-50 border border-transparent'}`}
                  >
                    <div className="flex justify-between items-center w-full mb-1">
                      <span className={`font-semibold truncate max-w-[140px] ${session.id === currentSessionId ? 'text-emerald-800' : 'text-slate-600'}`}>{session.name}</span>
                      <span className="text-[9px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 roundedtracking-wider">{session.language}</span>
                    </div>
                    <div className="flex justify-between items-center w-full mt-1 min-h-[24px]">
                      <span className="text-[10px] text-slate-400 font-medium">
                        {new Date(session.updatedAt).toLocaleDateString()} at {new Date(session.updatedAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </span>
                      {hoveredSessionId === session.id && (
                        <div className="flex items-center gap-1.5 animate-in fade-in duration-200">
                          <button
                            onClick={(e) => shareSession(e, session)}
                            disabled={isGeneratingLink}
                            className="p-1.5 rounded bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors tooltip-trigger disabled:opacity-50"
                            title="Share Link"
                          >
                            {isGeneratingLink ? <Loader2 size={12} className="animate-spin" /> : <Share2 size={12} />}
                          </button>
                          <button
                            onClick={(e) => requestDeleteSession(e, session.id)}
                            className="p-1.5 rounded bg-rose-50 text-rose-500 hover:bg-rose-100 transition-colors tooltip-trigger"
                            title="Delete Session"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {sessions.length === 0 && (
                  <div className="text-center py-6 text-sm text-slate-400 font-medium">No saved sessions</div>
                )}
              </div>
              <div className="p-3 bg-slate-50 border-t border-slate-100 flex flex-col gap-2">
                <button
                  onClick={() => { createNewSession('// Welcome to the Sandbox!\n// Write code here and add Custom Input to test specific edge cases.', 'javascript'); setShowHistory(false); }}
                  className="w-full flex items-center justify-center gap-2 py-2 text-[13px] font-bold text-emerald-700 bg-emerald-100 hover:bg-emerald-200 hover:shadow-sm rounded-lg transition-all"
                >
                  <Plus size={14} /> New Session
                </button>
              </div>
            </div>
          )}

          <div className="w-px h-4 bg-emerald-200 mx-1"></div>

          <button
            onClick={handleReset}
            className="p-1.5 rounded-md hover:bg-emerald-100/80 transition-colors text-slate-400 hover:text-emerald-700 tooltip-trigger"
            title="Reset code"
          >
            <RotateCcw size={15} />
          </button>
          <button
            onClick={handleCopy}
            className="p-1.5 flex items-center gap-1.5 rounded-md hover:bg-emerald-100/80 transition-colors text-slate-400 hover:text-emerald-700"
            title="Copy code"
          >
            {copied ? <Check size={15} className="text-emerald-600" /> : <Copy size={15} />}
          </button>
        </div>
      </div>

      {/* Monaco Editor Area */}
      <div className="relative flex bg-white w-full border-b border-emerald-50" style={{ height: editorHeight }}>
        <Editor
          height="100%"
          language={language === 'cpp' ? 'cpp' : language === 'go' ? 'go' : language}
          theme={isDarkMode ? 'vs-dark' : 'light'}
          value={code}
          onChange={(value) => setCode(value || '')}
          onMount={handleEditorDidMount}
          options={{
            minimap: { enabled: false },
            fontSize: 13,
            lineHeight: 22,
            fontFamily: "'JetBrains Mono', 'Fira Code', 'Roboto Mono', monospace",
            padding: { top: 16, bottom: 16 },
            scrollBeyondLastLine: false,
            smoothScrolling: true,
            cursorBlinking: 'smooth',
            cursorSmoothCaretAnimation: 'on',
            formatOnPaste: true,
            renderLineHighlight: 'all',
            scrollbar: {
              verticalScrollbarSize: 8,
              horizontalScrollbarSize: 8,
            },
          }}
        />
      </div>

      {/* Footer / Run Bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-white">
        <button
          onClick={() => setShowInput(!showInput)}
          className={`text-xs font-semibold flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-all ${
            showInput 
              ? 'bg-emerald-100 text-emerald-800' 
              : 'text-slate-500 hover:bg-emerald-50 hover:text-emerald-700 border border-transparent hover:border-emerald-200'
          }`}
        >
          <Terminal size={12} className={showInput ? 'text-emerald-600' : ''} />
          {showInput ? 'Hide Stdin Input' : 'Add Custom Input'}
        </button>

        <div className="flex items-center gap-3">
          <span className="text-[10px] text-slate-400 hidden sm:block font-medium tracking-wide border px-2 py-0.5 rounded-md border-slate-200 bg-slate-50">
            ⌘ + ENTER
          </span>
          <button
            onClick={handleRun}
            disabled={isRunning || !code.trim()}
            className="group/run flex items-center gap-2 px-5 py-2 rounded-md bg-emerald-500 text-white shadow-sm shadow-emerald-500/20 text-sm font-bold hover:bg-emerald-600 hover:shadow-emerald-500/30 dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary/90 dark:shadow-primary/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 active:scale-[0.98]"
          >
            {isRunning ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Play size={14} className="fill-current" />
            )}
            {isRunning ? 'Executing...' : 'Run Code'}
          </button>
        </div>
      </div>

      {/* Custom Input Area */}
      {showInput && (
        <div className="px-5 py-4 bg-emerald-50/30 border-t border-emerald-100">
          <label className="text-[10px] font-bold text-emerald-800tracking-widest block mb-2">
            Standard Input (stdin)
          </label>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="w-full bg-white border border-emerald-200 rounded-md p-3 text-[13px] font-mono text-slate-800 resize-none outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 transition-colors shadow-sm placeholder:text-slate-400"
            rows={2}
            placeholder="Type your runtime inputs here..."
          />
        </div>
      )}

      {/* Output Panel */}
      {(output || isRunning) && (
        <div className="border-t border-emerald-100 bg-slate-50/80 px-5 py-4 shadow-inner">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] font-bold text-emerald-800tracking-widest flex items-center gap-2">
              <Terminal size={12} className="text-emerald-600" /> Execution Output
            </span>
            {output && !output.error && (
              <div className="flex items-center gap-4 text-[10px] font-mono text-slate-500">
                {output.time && (
                  <span className="flex items-center gap-1.5" title="Execution Time">
                    <Clock size={11} className="text-blue-500" /> {output.time}s
                  </span>
                )}
                {output.memory && (
                  <span className="flex items-center gap-1.5" title="Memory Used">
                    <Cpu size={11} className="text-rose-500" /> {output.memory}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="bg-white rounded-md p-4 border border-slate-200 shadow-sm">
            {isRunning ? (
              <div className="flex items-center gap-2 text-emerald-600 text-[13px] font-mono font-medium">
                <Loader2 size={14} className="animate-spin" />
                Executing in secure sandbox...
              </div>
            ) : output ? (
              <div className="font-mono text-[13px] leading-relaxed whitespace-pre-wrap">
                {output.compile_output && (
                  <div className="mb-3">
                    <span className="text-amber-600 text-[11px] font-boldtracking-widest block mb-1">Compiler Details</span>
                    <span className="text-slate-700 bg-amber-50 border border-amber-100 px-3 py-2 rounded-md block">{output.compile_output}</span>
                  </div>
                )}
                {output.stdout && (
                  <div className="mb-2">
                    <span className="text-slate-800">{output.stdout}</span>
                  </div>
                )}
                {output.stderr && (
                  <div className="mb-3 pt-3 mt-2 border-t border-rose-100">
                    <span className="text-rose-500 text-[11px] font-boldtracking-widest block mb-1">Standard Error</span>
                    <span className="text-rose-700 bg-rose-50 border border-rose-100 px-3 py-2 rounded-md block">{output.stderr}</span>
                  </div>
                )}
                {output.error && (
                  <div className="text-rose-600 font-semibold">{output.error}</div>
                )}
                {!output.stdout && !output.stderr && !output.compile_output && !output.error && (
                  <span className="text-slate-500 italic">Program executed successfully but produced no output.</span>
                )}
                {output.status && output.status !== 'Accepted' && !output.error && (
                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-2 text-[11px] text-slate-500">
                    Exit Status: 
                    <span className={`px-2 py-0.5 rounded-full font-bold ${
                      output.status === 'Accepted' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {output.status}
                    </span>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal 
        isOpen={!!deleteSessionId} 
        onClose={() => setDeleteSessionId(null)}
        title="Delete Session"
        footer={
          <>
            <Button variant="outline" onClick={() => setDeleteSessionId(null)}>Cancel</Button>
            <Button variant="danger" onClick={confirmDeleteSession}>Delete</Button>
          </>
        }
      >
        <p className="text-slate-600">Are you sure you want to delete this session? This action cannot be undone.</p>
      </Modal>

      {/* Share Modal */}
      <Modal 
        isOpen={!!shareSessionUrl} 
        onClose={() => setShareSessionUrl(null)}
        title="Share Session"
      >
        <div className="flex flex-col gap-4 pb-2">
          <p className="text-slate-600 text-[13px] font-medium leading-relaxed">
            Anyone with this link will be able to instantly view and run this exact code session.
          </p>
          <div className="flex items-center gap-2">
            <input 
              type="text" 
              readOnly 
              value={shareSessionUrl || ''} 
              className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] text-slate-600 focus:outline-none focus:border-emerald-400 font-mono overflow-hidden text-ellipsis shadow-inner"
              onClick={(e) => e.target.select()}
            />
            <Button variant="secondary" onClick={handleCopyShareLink} className="shadow-sm">
              {linkCopied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
              <span className="ml-1.5 font-bold">{linkCopied ? "Copied!" : "Copy"}</span>
            </Button>
          </div>
          
          <div className="mt-4 pt-5 border-t border-slate-100">
            <h4 className="text-[11px] font-bold text-slate-400tracking-widest mb-3 text-center">Share via</h4>
            <div className="flex gap-2">
              <a 
                href={`https://api.whatsapp.com/send?text=Check out my code session on prepxai! ${encodeURIComponent(shareSessionUrl)}`}
                target="_blank"
                rel="noreferrer"
                className="flex flex-1 justify-center items-center gap-1.5 px-3 py-2.5 bg-[#25D366]/10 text-[#25D366] hover:bg-[#25D366]/20 hover:scale-[1.02] active:scale-[0.98] rounded-xl transition-all text-xs font-black tracking-wide"
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg>
                WhatsApp
              </a>
              <a 
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareSessionUrl)}`}
                target="_blank"
                rel="noreferrer"
                className="flex flex-1 justify-center items-center gap-1.5 px-3 py-2.5 bg-[#0077b5]/10 text-[#0077b5] hover:bg-[#0077b5]/20 hover:scale-[1.02] active:scale-[0.98] rounded-xl transition-all text-xs font-black tracking-wide"
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                LinkedIn
              </a>
              <a 
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareSessionUrl)}&text=Check out my code session on prepxai!`}
                target="_blank"
                rel="noreferrer"
                className="flex flex-1 justify-center items-center gap-1.5 px-3 py-2.5 bg-slate-100 text-slate-700 hover:bg-slate-200 hover:scale-[1.02] active:scale-[0.98] rounded-xl transition-all text-xs font-black tracking-wide"
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                X (Twitter)
              </a>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CodePlayground;
